<template>
  <div>
    <!-- /*** FIRST NAME ***/ -->
    <div class="control">
      <label
        for="witness-first-name"
        class="text-question"
        v-html="content.LabelAndTooltipWitnessFirstname"
      />
      <Field
        v-slot="{ field }"
        v-model="localWitness.ContactInformation.FirstName"
        name="witness-first-name"
        :rules="{ required: !isWitnessValid, max: 50 }"
      >
        <base-text-input v-bind="field" :errors="errors" maxlength="50" />
      </Field>
    </div>
    <!-- /*** LAST NAME ***/ -->
    <div class="control">
      <label
        for="witness-last-name"
        class="text-question"
        v-html="content.LabelAndTooltipWitnessLastname"
      />
      <Field
        v-slot="{ field }"
        v-model="localWitness.ContactInformation.LastName"
        name="witness-last-name"
        :rules="{ required: !isWitnessValid, max: 50 }"
      >
        <base-text-input v-bind="field" :errors="errors" maxlength="50" />
      </Field>
    </div>
    <!-- /*** ADDRESS SEARCH ***/ -->
    <div class="control">
      <label
        for="witness-address"
        class="text-question"
        v-html="content.LabelAndTooltipWitnessAddressSearch"
      />
      <Field
        :model-value="localWitness.ContactInformation.SelectedAddress"
        v-slot="{ field }"
        name="witness-address"
        :rules="{ required: !isWitnessValid }"
      >
        <address-search
          v-bind="field" 
          :model-value="localWitness.ContactInformation.SelectedAddress"
          @update:model-value="(newValue) => { localWitness.ContactInformation.SelectedAddress = newValue; }"
          name="witness-address"
          :errors="errors"
        />
      </Field>
    </div>
    <div class="control row">
      <!-- /*** PHONE ***/ -->
      <div class="small-12 medium-6 columns">
        <label
          for="witness-phone"
          class="text-question"
          v-html="content.LabelAndTooltipWitnessPhone"
        />
        <Field
          v-model="localWitness.ContactInformation.Phone"
          v-slot="{ field }"
          name="witness-phone"
          :rules="{ required: !isWitnessValid, numeric: true, min: 8 }"
        >
          <base-text-input v-bind="field" maxlength="8" :errors="errors" />
        </Field>
      </div>
      <!-- /*** EMAIL ***/ -->
      <div class="small-12 medium-6 columns">
        <label
          for="witness-email"
          class="text-question"
          v-html="content.LabelAndTooltipWitnessEmail"
        />
        <Field
          v-model="localWitness.ContactInformation.Email"
          v-slot="{ field }"
          name="witness-email"  
          :rules="{ required: !isWitnessValid, email: true }"
        >
          <base-text-input v-bind="field" :errors="errors" />
        </Field>
      </div>
    </div>
    <Field as="hidden" name="witness-form" :rules="{ required: true }" :model-value="witnessValid" />
    <Field as="hidden" name="witness-form-go-next" :rules="{ required: isWitnessFormClosed() }" /> 
    <ValidationError
      data-test-witness-error
      name="witness-form"
      :overrides="{ required: content.ErrorMessageWitness }"
      :errors="errors"
    />        
    <div class="row">
      <div class="small-12 medium-6 columns">
        <button
          v-if="editingWitness"
          class="button secondary"
          @click="deleteWitness"
          data-test="delete-witness-button"
          v-html="deleteWitnessButtontext"
        />
      </div>
      <div class="small-12 medium-6 columns">
        <button
          class="button"
          @click="saveWitness"
          data-test-save-witness-button
          v-html="editingWitness ? saveChangesButtonText : saveWitnessButtonText"
        />
        <ValidationError
        data-test-witness-error
        name="witness-form-go-next"
        :overrides="{ required: getWitnessSaveChangeErrorText() }"
        :errors="errors"
      />        
      </div>      
    </div>    
</div>
</template>

<script>
import { defineRule } from 'vee-validate';
import AddressSearch from '@/components/UI/AddressSearch.vue';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import Utility from '@/common/UtilityFunctions.js';
import { cloneDeep, isEmpty } from 'lodash';
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';

export default {
  name: 'Witness',
  components: {
    AddressSearch,
    BaseTextInput,
    ValidationError
  },
  props: {
    modelValue: {
      type: Object,
      default() {
        return null;
      }
    },
    content: {
      type: Object,
      default() {
        return null;
      }
    },
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    countryCode: {
      type: Number,
      default() {
        return null;
      }
    },
    editingWitness: {
      type: Boolean,
      default() {
        return false;
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      localWitness: cloneDeep(this.modelValue),
      saveWitnessButtonText: this.content.AddWitnessText,
      saveChangesButtonText: this.content.SaveChangesText,
      deleteWitnessButtontext: this.content.DeleteWitnessText,
      witnessValid: null      
    };
  },
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  computed: {
    camelizedOptions() {
      return Utility.camelizeKeys(this.options);
    },
    isWitnessValid() {

      let nameAddressFilled = !isEmpty(this.localWitness.ContactInformation.FirstName) && !isEmpty(this.localWitness.ContactInformation.LastName) && !isEmpty(this.localWitness.ContactInformation.SelectedAddress);
      let emailAndPhoneFilled = !isEmpty(this.localWitness.ContactInformation.Email)&& !isEmpty(this.localWitness.ContactInformation.Phone);

      if (nameAddressFilled || emailAndPhoneFilled) {       
        this.setWitnessValid();
        return true;
      }

      return false;
    }
  },
  created() {    
  },
    watch: {
    'modelValue': function(val) {
      this.localWitness = val;      
    }
  },
  mounted() {
    if (
      this.modelValue === null ||
      this.content === null ||
      this.options === null ||
      this.countryCode === null
    ) {
      return;
    }
  },
  methods: {
    getWitnessSaveChangeErrorText() {
      if (this.editingWitness)
        return this.content.ErrorMessageSaveEditedWitnessBeforeGoToNextStep;
      else
        return this.content.ErrorMessageSaveNewWitnessBeforeGoToNextStep;
    },
    setWitnessValid() {      
      this.witnessValid = true;
    },

    isWitnessFormClosed() {
      return this.localWitness && this.witnessValid == true;
    },

    async saveWitness() {

      let firstNameResult = await this.form.validateField('witness-first-name');
      let lastNameResult = await this.form.validateField('witness-last-name');
      let addressResult = await this.form.validateField('witness-address');
      let phoneResult = await this.form.validateField('witness-phone');
      let emailResult = await this.form.validateField('witness-email');
      
      this.witnessValid = firstNameResult.valid && lastNameResult.valid && addressResult.valid && phoneResult.valid && emailResult.valid;

      if (this.witnessValid) { 
        if (this.localWitness.ContactInformation.SelectedAddress !== null && this.localWitness.ContactInformation.SelectedAddress !== undefined) {
        this.localWitness.ContactInformation.Address.Street = this.localWitness.ContactInformation.SelectedAddress.adresse.vejnavn;
        this.localWitness.ContactInformation.Address.HouseNumber = this.localWitness.ContactInformation.SelectedAddress.adresse.husnr;
        this.localWitness.ContactInformation.Address.Floor = this.localWitness.ContactInformation.SelectedAddress.adresse.etage;
        this.localWitness.ContactInformation.Address.Designation = this.localWitness.ContactInformation.SelectedAddress.adresse.dør;
        this.localWitness.ContactInformation.Address.District = this.localWitness.ContactInformation.SelectedAddress.adresse.supplerendebynavn;
        this.localWitness.ContactInformation.Address.PostalCode = this.localWitness.ContactInformation.SelectedAddress.adresse.postnr;
        this.localWitness.ContactInformation.Address.City = this.localWitness.ContactInformation.SelectedAddress.adresse.postnrnavn;
        this.localWitness.ContactInformation.Address.CountryRef = this.countryCode;
        }
      
      this.$emit('save-witness', this.localWitness);
      this.resetWitness();
      }      
    },
    deleteWitness() {
      this.$emit('delete-witness');
      this.resetWitness();
    },
    resetWitness() {
      this.localWitness = {
        ContactInformation: {
          FirstName: null,
          LastName: null,
          Phone: null,
          Email: null,
          Address: {
            Street: null,
            HouseNumber: null,
            Floor: null,
            Designation: null,
            District: null,
            PostalCode: null,
            City: null,
            CountryRef: this.countryCode
          },
          SelectedAddress: null,
        }
      };
    }
  }
};
</script>